// Shared constants and helpers for the model grid and its detail modal.

export const TYPE_LABELS = {
  text: '文本',
  image: '图像',
  audio: '语音',
  embedding: '向量',
  video: '视频',
};

export const WIRE_FORMAT_LABELS = {
  'openai':    'OpenAI',
  'anthropic': 'Anthropic',
  'ali-video': '万象视频',
  'ali-image': '万象图像',
  'sd-video': 'Seedance' ,
  'sd-image': 'Seedream' ,
  'minimax-video': 'MiniMax 视频',
  'sd-audio': 'Seed 音频',
  'minimax-audio': 'MiniMax 语音',
};

export const WIRE_FORMAT_DOCS = {
  'openai':    'https://developers.openai.com/api/reference/resources/chat/subresources/completions/methods/create',
  'anthropic': 'https://docs.anthropic.com/en/api/messages',
  'ali-video': 'https://bailian.console.aliyun.com/cn-beijing/?tab=api#/api/?type=model&url=2865250',
  'ali-image': 'https://bailian.console.aliyun.com/cn-beijing/?tab=api#/api/?type=model&url=3026980',
  'sd-video': 'https://www.volcengine.com/docs/82379/1520757?lang=zh',
  'sd-image': 'https://www.volcengine.com/docs/82379/1541523',
  'minimax-video': 'https://platform.minimaxi.com/docs/api-reference/video-generation-v2-create',
  'minimax-audio': 'https://platform.minimaxi.com/docs/api-reference/speech-t2a-http',
  'embeddings': 'https://developers.openai.com/api/reference/resources/embeddings/methods/create',
};

export function trimModelName(value) {
  return typeof value === 'string' ? value.trim() : value || '';
}

// 错峰计费预设，与后端 model/time_discount.go 一一对应，改一处必须改另一处。
// text 只是管理员在能力页选择时的简称；展示给用户的完整说明由后端随模型下发
// (time_discount_label)，避免两边文案走样。
export const TIME_DISCOUNT_OPTIONS = [
  { key: '', text: '不打折', value: '' },
  {
    key: 'preset1',
    text: '错峰五折（工作日 9-12、14-18 原价）',
    value: 'preset1',
  },
];

const ICON_RULES = [
  { match: 'qwen', icon: '/icons/qwen.png', provider: 'Qwen' },
  { match: 'wan', icon: '/icons/qwen.png', provider: 'Qwen' },
  { match: 'happyhorse', icon: '/icons/qwen.png', provider: 'Qwen' },
  { match: 'kimi', icon: '/icons/kimi.png', provider: 'Kimi' },
  { match: 'deepseek', icon: '/icons/deepseek.png', provider: 'DeepSeek' },
  { match: 'minimax', icon: '/icons/minimax.png', provider: 'MiniMax' },
  { match: 'glm', icon: '/icons/z-icon.png', provider: 'GLM' },
  { match: 'claude', icon: '/icons/claude.png', provider: 'Claude' },
  { match: 'gpt', icon: '/icons/openai.png', provider: 'OpenAI' },
  { match: 'grok', icon: '/icons/grok.png', provider: 'Grok' },
  { match: 'doubao-seed', icon: '/icons/seed.jpg', provider: 'Seed' },
  { match: 'seedance', icon: '/icons/seed.jpg', provider: 'Seed' },
];

export function getModelMeta(modelName) {
  const lower = modelName.toLowerCase();
  const rule = ICON_RULES.find(({ match }) => lower.includes(match));
  return rule || { icon: '/logo.png', provider: 'Model' };
}

// backend returns CNY/1K tokens, display as CNY/1M
export function formatPrice(pricePerK) {
  if (pricePerK === 0 || Number(pricePerK) === 0) return '免费';
  const pricePerM = Number(pricePerK) * 1000;
  return parseFloat(pricePerM.toPrecision(4)).toString();
}

// Token counts read better as 128K / 1M than as 128000 / 1000000. Values that
// don't land on a round K/M boundary keep one decimal rather than being rounded
// into a wrong-looking threshold.
export function formatTokenCount(tokens) {
  const value = Number(tokens || 0);
  if (value <= 0) return '0';
  const trim = (n) => parseFloat(n.toFixed(1)).toString();
  if (value >= 1000000) return `${trim(value / 1000000)}M`;
  if (value >= 1000) return `${trim(value / 1000)}K`;
  return value.toString();
}

export function formatCardPrice(pricePerK) {
  const value = Number(pricePerK || 0);
  if (value === 0) return '免费';
  const pricePerM = value * 1000;
  const rounded = Math.round(pricePerM * 10000) / 10000;
  return rounded.toString();
}

// For video models: input_price is the base rate (1000 * modelRatio / quotaPerUnit).
// Price per second at a given resolution = input_price * resRatio.
export function parseResolutionRatios(raw) {
  if (!raw) return [];
  try {
    const obj = JSON.parse(raw);
    return Object.entries(obj)
      .map(([resolution, ratio]) => ({ resolution, ratio: Number(ratio) }))
      .sort((a, b) => a.ratio - b.ratio);
  } catch {
    return [];
  }
}

export function formatVideoPricePerSec(inputPrice, resRatio) {
  const perSec = Number(inputPrice) * Number(resRatio);
  if (perSec === 0) return '免费';
  return parseFloat(perSec.toPrecision(4)).toString();
}

// Seedance is priced by a hard-coded rate table upstream (see
// calcSdVideoEstimatedQuota in relay/controller/seedance/sd_video.go), not by
// model_ratio, so input_price says nothing about it. Mirror that table here and
// apply the channel's sd_discount. Match order matters: mini/fast must be tested
// before the bare 2.0 prefix, exactly as the Go switch does.
const SEEDANCE_RATES = [
  {
    match: ['seedance-2-5', 'seedance-2.5'],
    rows: [{ resolution: '480P / 720P', yuanPerSec: 1.5 }],
  },
  {
    match: ['seedance-2-0-mini', 'seedance-2.0-mini'],
    rows: [{ resolution: '单价', yuanPerSec: 0.5 }],
  },
  {
    match: ['seedance-2-0-fast', 'seedance-2.0-fast'],
    rows: [{ resolution: '480P / 720P', yuanPerSec: 0.8 }],
  },
  {
    match: ['seedance-2-0', 'seedance-2.0'],
    rows: [
      { resolution: '480P / 720P', yuanPerSec: 1.0 },
      { resolution: '1080P', yuanPerSec: 2.4 },
    ],
  },
];

const SEEDANCE_DEFAULT_ROWS = [{ resolution: '单价', yuanPerSec: 1.0 }];

function isSeedanceVideo(model) {
  return (
    (model?.wire_formats || '').includes('sd-video') ||
    (model?.model || '').toLowerCase().includes('seedance')
  );
}

// A never-set (0) or negative discount reads as list price, matching
// sdDiscountOrList on the backend — never as free.
function seedanceDiscount(model) {
  const discount = Number(model?.sd_discount || 0);
  return discount > 0 ? discount : 1;
}

// Per-second price rows for a video model: [{label, price}] with price already
// formatted (a bare number, or '免费'). Wrap with formatVideoPrice to display.
export function getVideoPriceRows(model) {
  if (isSeedanceVideo(model)) {
    const name = (model?.model || '').toLowerCase();
    const entry = SEEDANCE_RATES.find(({ match }) =>
      match.some((prefix) => name.includes(prefix))
    );
    const discount = seedanceDiscount(model);
    return (entry ? entry.rows : SEEDANCE_DEFAULT_ROWS).map(
      ({ resolution, yuanPerSec }) => ({
        label: resolution,
        price: formatVideoPricePerSec(yuanPerSec, discount),
      })
    );
  }
  const ratios = parseResolutionRatios(model?.resolution_ratios);
  if (ratios.length === 0) {
    return [{ label: '单价', price: formatVideoPricePerSec(model?.input_price, 1) }];
  }
  return ratios.map(({ resolution, ratio }) => ({
    label: resolution,
    price: formatVideoPricePerSec(model?.input_price, ratio),
  }));
}

// The cheapest row — what the grid card shows next to its 起 label.
export function getVideoCardPriceRow(model) {
  const rows = getVideoPriceRows(model);
  return rows.reduce(
    (cheapest, row) =>
      Number(row.price) < Number(cheapest.price) ? row : cheapest,
    rows[0]
  );
}

// Every video price is an estimate — the settled charge depends on the actual
// output — so they all render as 约 X¥/S.
export function formatVideoPrice(price) {
  return price === '免费' ? '免费' : `约 ${price}¥/S`;
}

export function formatImageCardPrice(inputPrice) {
  const value = Number(inputPrice || 0);
  if (value === 0) return '免费';
  return parseFloat(value.toPrecision(4)).toString();
}

// 音频按时长计费，与视频同口径：后端 input_price = 1000 * model_ratio / QuotaPerUnit(1M)，
// 直接就是 元/分钟（model_ratio=1000 -> 1 元/分钟）。
export function formatAudioCardPrice(inputPrice) {
  const value = Number(inputPrice || 0);
  if (value === 0) return '免费';
  return parseFloat(value.toPrecision(4)).toString();
}

// MiniMax 语音按计费字符数收费（model_ratio=1000 -> 1 元/万字符），其它音频按时长。
export function getAudioPriceUnit(wireFormats) {
  return (wireFormats || '').includes('minimax-audio') ? '万字符' : '分钟';
}

export function getApiEndpoint() {
  const status = JSON.parse(localStorage.getItem('status') || '{}');
  return (
    (
      status.api_address ||
      status.server_address ||
      window.location.origin
    ).replace(/\/$/, '') + '/v1'
  );
}

// Returns [{label, path}] for the API paths this model supports.
// sdChannelIds：sd-video 模型可用的渠道 id，用来填 KYC 地址里的 ?c=。
export function getApiPaths(wireFormats, type, sdChannelIds) {
  const wf = wireFormats || 'openai';
  // Embedding models expose the dedicated OpenAI embeddings endpoint only.
  if (type === 'embedding') {
    return [{ label: 'OpenAI Embeddings', path: '/embeddings', docUrl: WIRE_FORMAT_DOCS['embeddings'] }];
  }
  const paths = [];
  if (wf.includes('openai')) {
    paths.push({ label: 'OpenAI Chat', path: '/chat/completions', docUrl: WIRE_FORMAT_DOCS['openai'] });
    paths.push({ label: 'OpenAI Responses', path: '/responses' });
  }
  if (wf.includes('anthropic')) {
    paths.push({ label: 'Anthropic Messages', path: '/messages', docUrl: WIRE_FORMAT_DOCS['anthropic'] });
  }
  if (wf.includes('ali-video')) {
    paths.push({ label: '阿里万象视频 (提交)', path: '/wan/video/generation', docUrl: WIRE_FORMAT_DOCS['ali-video'] });
    paths.push({ label: '任务查询', path: '/tasks/{task_id}', docUrl: WIRE_FORMAT_DOCS['ali-video'] });
  }
  if (wf.includes('ali-image')) {
    paths.push({ label: '阿里万象图像', path: '/wan/images/generations', docUrl: WIRE_FORMAT_DOCS['ali-image'] });
  }
  if (wf.includes("sd-video")) {
    paths.push({ label: 'Seedance', path: '/sd/video/generation', docUrl: WIRE_FORMAT_DOCS['sd-video'] });
    paths.push({ label: '任务查询', path: '/tasks/{task_id}' });
    // 资产库按渠道隔离，c 必须是该模型实际所在的渠道 id。
    // 后端已返回本分组可用的 sd-video 渠道，有几个就列几条；拿不到时退回占位符。
    const kycDocUrl = 'https://www.volcengine.com/docs/82379/2333565?redirect=1&lang=zh';
    const ids = sdChannelIds || [];
    if (ids.length === 0) {
      paths.push({
        label: '资产库（不同seedance模型资产库可能会有隔离，请做好联调）',
        path: '/sd/kyc/{action}?c={id}',
        docUrl: kycDocUrl,
      });
    } else {
      ids.forEach((id) => {
        paths.push({
          label: ids.length > 1
            ? `资产库 · 渠道 ${id}（各渠道资产库相互隔离，请固定使用同一渠道）`
            : '资产库（资产库按渠道隔离，请固定使用该渠道）',
          path: `/sd/kyc/{action}?c=${id}`,
          docUrl: kycDocUrl,
        });
      });
    }
  }
  if (wf.includes('sd-image')) {
    paths.push({ label: 'Seedream', path: '/sd/images/generations', docUrl: WIRE_FORMAT_DOCS['sd-image'] });
  }
  if (wf.includes('sd-audio')) {
    paths.push({ label: 'Seed 音频', path: '/sd/audio/generations' });
  }
  if (wf.includes('minimax-video')) {
    paths.push({ label: 'MiniMax 视频 (提交)', path: '/minimax/video/generation', docUrl: WIRE_FORMAT_DOCS['minimax-video'] });
    paths.push({ label: '任务查询', path: '/tasks/{task_id}' });
    paths.push({ label: '取消/删除任务', path: '/tasks/{task_id}' });
  }
  if (wf.includes('minimax-audio')) {
    paths.push({ label: 'MiniMax 语音', path: '/minimax/audio/generation', docUrl: WIRE_FORMAT_DOCS['minimax-audio'] });
  }
  return paths.length > 0
    ? paths
    : [{ label: 'OpenAI', path: '/chat/completions' }];
}

export function getWireFormatList(wireFormats) {
  if (!wireFormats) return ['openai'];
  return wireFormats
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

export function getTagList(tags) {
  if (!tags) return [];
  return tags
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}
